import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { usePageMeta } from '../../../hooks/usePageMeta';

const RefundPage: React.FC = () => {
  usePageMeta(
    'Chính sách Hoàn tiền | Dịch Tài Liệu',
    'Chính sách hoàn tiền credit dịch tài liệu AI. Credit đã thanh toán không hoàn lại trừ các trường hợp theo quy định pháp luật.',
    'https://dichtailieu.vn/refund',
  );
  return (
    <div className="min-h-screen bg-white">
      <nav className="px-6 py-4 border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-indigo-600">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Về trang chủ</span>
          </Link>
          <div className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-indigo-600" />
            <span className="font-black text-slate-800">Chính sách Hoàn tiền</span>
          </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-black text-slate-900 mb-2">Chính sách Hoàn tiền</h1>
        <p className="text-slate-500 text-sm mb-8">Cập nhật lần cuối: 22/04/2026</p>

        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-8">
          <p className="text-slate-700 leading-relaxed">
            <strong>Tóm tắt</strong>: Credit đã thanh toán không hoàn lại, trừ các trường hợp nêu cụ thể dưới đây hoặc khi pháp luật bắt buộc. Credit là đơn vị trả trước để sử dụng dịch vụ dịch tài liệu số — khi bạn thanh toán, bạn cam kết dùng credit đó cho dịch vụ dịch.
          </p>
        </div>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">1. Nguyên tắc chung</h2>
        <p className="text-slate-700 leading-relaxed mb-3">
          Credit là đơn vị trả trước để sử dụng dịch vụ dịch tài liệu số. <strong>Credit đã thanh toán không hoàn lại, trừ các trường hợp nêu dưới đây hoặc trường hợp pháp luật Việt Nam bắt buộc Dịch Tài Liệu phải hoàn tiền/bồi thường.</strong>
        </p>
        <p className="text-slate-700 leading-relaxed mb-2">
          Trước khi thanh toán, bạn sẽ được hiển thị:
        </p>
        <ul className="list-disc pl-6 text-slate-700 space-y-2">
          <li>Giá gói và số credit nhận được (<Link to="/pricing" className="text-indigo-600 hover:underline">/pricing</Link>)</li>
          <li><Link to="/tos" className="text-indigo-600 hover:underline">Điều khoản sử dụng</Link> và Chính sách Hoàn tiền này</li>
          <li>Checkbox xác nhận <strong>"Tôi đã đọc và hiểu credit đã thanh toán không hoàn lại, trừ trường hợp nêu trong Chính sách Hoàn tiền"</strong> (chưa tick = không thể hoàn tất giao dịch)</li>
        </ul>
        <p className="text-slate-700 leading-relaxed mt-3">
          Sau khi thanh toán, bạn nhận được biên lai giao dịch qua email, kèm link tải lại TOS và Chính sách Hoàn tiền tại thời điểm mua.
        </p>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">2. Các trường hợp được hoàn tiền theo chính sách</h2>

        <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">A. Trùng lặp giao dịch</h3>
        <p className="text-slate-700 leading-relaxed">
          Bạn bị tính tiền 2 lần cho cùng 1 đơn hàng do lỗi hệ thống thanh toán → hoàn 100% lần tính tiền thứ 2.
        </p>

        <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">B. Lỗi hệ thống cộng sai credit</h3>
        <p className="text-slate-700 leading-relaxed">
          Bạn thanh toán gói 1.5M credits nhưng hệ thống chỉ cộng 1M credits → hoàn tiền cho phần chênh lệch, hoặc cộng đủ credit còn thiếu.
        </p>

        <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">C. Dịch vụ không thể sử dụng liên tiếp {'>'} 72 giờ</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          Nếu toàn bộ dịch vụ không thể sử dụng được trong <strong>hơn 72 giờ liên tiếp</strong> — dù do hệ thống của Dịch Tài Liệu hay do nhà cung cấp hạ tầng cốt lõi (Google Gemini / Cloudflare / Supabase) gián đoạn — bạn có quyền yêu cầu:
        </p>
        <ul className="list-disc pl-6 text-slate-700 space-y-1">
          <li>Hoàn phần credit chưa sử dụng theo tỉ lệ, hoặc</li>
          <li>Gia hạn/bù credit tương đương thời gian gián đoạn</li>
        </ul>
        <p className="text-slate-700 leading-relaxed mt-2 text-sm italic">
          Không áp dụng cho: gián đoạn dưới 72 giờ, bảo trì đã thông báo trước, lỗi bên phía bạn (mạng internet, thiết bị, tài khoản Google), lỗi do cấu hình sai của bạn.
        </p>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">3. Các trường hợp KHÔNG hoàn tiền theo chính sách</h2>
        <p className="text-slate-700 leading-relaxed mb-2">
          Chính sách này không cho phép hoàn tiền trong các trường hợp sau (nhưng quyền của bạn theo pháp luật vẫn được bảo vệ — xem Mục 4):
        </p>
        <ul className="list-disc pl-6 text-slate-700 space-y-2">
          <li><strong>Chất lượng bản dịch không như mong đợi</strong> — AI có thể dịch sai. Bạn có trách nhiệm tự kiểm tra (xem <Link to="/tos" className="text-indigo-600 hover:underline">TOS Mục 7</Link>). Dùng gói Thử 19.000đ để đánh giá chất lượng trước khi mua gói lớn hơn.</li>
          <li><strong>Thay đổi ý định</strong>, không còn nhu cầu sử dụng</li>
          <li><strong>Đã sử dụng một phần credit</strong> và muốn hoàn phần còn lại (vì không tính là lỗi dịch vụ)</li>
          <li><strong>Xóa tài khoản</strong> — credits còn lại bị mất khi xóa tài khoản</li>
          <li><strong>Tạm dừng sử dụng</strong> — credit không hết hạn trong giai đoạn beta, bạn quay lại bất cứ lúc nào</li>
          <li><strong>Gián đoạn dưới 72 giờ</strong> — bảo trì, lỗi ngắn, không thuộc Mục 2C</li>
          <li><strong>Vi phạm điều khoản</strong> — tài khoản bị tạm khóa do lạm dụng (<Link to="/tos" className="text-indigo-600 hover:underline">TOS Mục 8</Link>), credit không hoàn</li>
        </ul>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">4. Quyền của bạn theo pháp luật (không bị hạn chế bởi chính sách này)</h2>
        <p className="text-slate-700 leading-relaxed mb-2">
          Chính sách này <strong>không thay thế hoặc loại trừ</strong> các quyền của bạn theo Luật Bảo vệ Quyền lợi Người tiêu dùng Việt Nam 2023, bao gồm nhưng không giới hạn:
        </p>
        <ul className="list-disc pl-6 text-slate-700 space-y-2">
          <li>Quyền khiếu nại nếu dịch vụ không đúng như công bố/cam kết</li>
          <li>Quyền yêu cầu bồi thường theo quy định pháp luật</li>
          <li>Quyền khiếu nại lên cơ quan có thẩm quyền (Cục Cạnh tranh và Bảo vệ Người tiêu dùng — Bộ Công Thương)</li>
        </ul>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">5. Quy trình yêu cầu hoàn tiền</h2>
        <p className="text-slate-700 leading-relaxed mb-2">
          Nếu thuộc Mục 2 (trường hợp được hoàn tiền theo chính sách):
        </p>
        <ol className="list-decimal pl-6 text-slate-700 space-y-2">
          <li>Gửi email đến <a href="mailto:support@dichtailieu.vn" className="text-indigo-600 hover:underline">support@dichtailieu.vn</a> với tiêu đề <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">[Refund] &lt;Mã giao dịch&gt;</code></li>
          <li>Nội dung email:
            <ul className="list-disc pl-6 mt-1 space-y-1 text-sm">
              <li>Email tài khoản + Mã giao dịch (xem Lịch sử giao dịch)</li>
              <li>Số tiền + ngày thanh toán</li>
              <li>Lý do (thuộc A, B, hay C ở Mục 2) + bằng chứng nếu có</li>
            </ul>
          </li>
          <li>Phản hồi trong <strong>5 ngày làm việc</strong></li>
          <li>Nếu duyệt: hoàn tiền qua chuyển khoản ngân hàng trong <strong>7 ngày làm việc</strong> (không hoàn qua Momo/ví điện tử do giới hạn kỹ thuật)</li>
          <li>Nếu từ chối: chúng tôi giải thích rõ lý do</li>
        </ol>
        <p className="text-slate-700 leading-relaxed mt-3">
          <strong>Thời hạn</strong>: yêu cầu hoàn tiền theo chính sách này phải gửi trong <strong>30 ngày</strong> kể từ ngày phát sinh. Thời hạn 30 ngày chỉ áp dụng cho các trường hợp hoàn tiền theo chính sách này; <strong>không hạn chế quyền khiếu nại/yêu cầu bồi thường của bạn theo pháp luật</strong>.
        </p>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">6. Lời khuyên trước khi thanh toán</h2>
        <ul className="list-disc pl-6 text-slate-700 space-y-2">
          <li><strong>Thử gói 19.000đ trước</strong> — test chất lượng AI cho loại tài liệu của bạn</li>
          <li><strong>Đọc <Link to="/tos" className="text-indigo-600 hover:underline">Disclaimer AI (TOS Mục 7)</Link></strong> — AI có thể dịch sai, không phù hợp cho tài liệu pháp lý/y tế/tài chính quan trọng</li>
          <li><strong>Chọn gói vừa nhu cầu</strong> — credit không hết hạn nhưng không nên mua vượt quá thực tế</li>
        </ul>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">7. Giải quyết tranh chấp</h2>
        <ul className="list-disc pl-6 text-slate-700 space-y-2">
          <li>Thương lượng qua email <a href="mailto:support@dichtailieu.vn" className="text-indigo-600 hover:underline">support@dichtailieu.vn</a> (phản hồi 5 ngày làm việc)</li>
          <li>Tranh chấp dưới 100 triệu VND thương lượng không thành: có thể khiếu nại đến <strong>Cục Cạnh tranh và Bảo vệ Người tiêu dùng</strong> (Bộ Công Thương)</li>
          <li>Tranh chấp từ 100 triệu VND trở lên: Tòa án nhân dân có thẩm quyền tại TP.HCM (theo <Link to="/tos" className="text-indigo-600 hover:underline">TOS Mục 11</Link>)</li>
        </ul>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">8. Thay đổi chính sách</h2>
        <p className="text-slate-700 leading-relaxed">
          Chính sách hoàn tiền có thể được cập nhật. Thay đổi sẽ thông báo qua email và/hoặc banner trong app <strong>ít nhất 7 ngày trước khi có hiệu lực</strong>. Giao dịch đã hoàn tất trước ngày thay đổi sẽ áp dụng chính sách tại thời điểm thanh toán.
        </p>

        <h2 className="text-xl font-bold text-slate-800 mt-8 mb-3">9. Liên hệ</h2>
        <p className="text-slate-700 leading-relaxed">
          Email: <a href="mailto:support@dichtailieu.vn" className="text-indigo-600 hover:underline">support@dichtailieu.vn</a> — phản hồi trong 5 ngày làm việc.
        </p>

        <p className="text-slate-500 text-sm mt-12 pt-6 border-t border-slate-200">
          Xem thêm <Link to="/tos" className="text-indigo-600 hover:underline">Điều khoản sử dụng</Link> ·
          {' '}<Link to="/privacy" className="text-indigo-600 hover:underline">Chính sách bảo mật</Link>
        </p>
      </article>
    </div>
  );
};

export default RefundPage;
